import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationCity as BranchIcon,
  Business as SalesPointIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  AccountBalance as SettlementIcon,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import HolderAvatar from "../../components/layout/HolderAvatar";
import { userAPI } from "../../services/userAPI";
import {
  auditLogsAPI,
  AuditLogEntry,
  AuditLogSortField,
} from "../../services/auditLogsAPI";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import settlementAPI, { PendingSettlement } from "../../services/settlementAPI";
import { useRBAC } from "../../hooks/useRBAC";
import { useAuthStore } from "../../stores/authStore";
import { tokens } from "../../theme";
import { formatRoleLabel } from "../../constants/roleLabels";

const MPIN_ROLES = new Set(["OPERATOR", "MERCHANT", "GROCERY_STORE"]);

const formatWhen = (value: string) => {
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm:ss");
  } catch {
    return value;
  }
};

const roleColor = (role: string) => {
  switch (role) {
    case "SUPER_ADMIN":
      return "error";
    case "ADMIN":
      return "warning";
    case "SALES_POINT_ADMIN":
      return "info";
    case "OPERATOR":
      return "info";
    case "MERCHANT":
      return "secondary";
    default:
      return "default";
  }
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const summaryBody = (body: Record<string, unknown> | null) => {
  if (!body) return "—";
  const text = JSON.stringify(body);
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
};

const formatInr = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n ?? 0);

const UserProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canUpdate } = useRBAC();
  const { user: currentUser } = useAuthStore();

  const roleUpper = String(currentUser?.role || "").toUpperCase();
  const isSuperAdmin =
    roleUpper === "SUPER_ADMIN" || roleUpper === "SUPERADMIN";
  const isBranchAdmin = roleUpper === "ADMIN";
  const canResetCredentials = isSuperAdmin || isBranchAdmin;
  const canViewAuditLogs = canResetCredentials;

  const [auditPage, setAuditPage] = useState(0);
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(10);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newMpin, setNewMpin] = useState("");
  const [resetMsg, setResetMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const { sortBy: auditSortBy, sortOrder: auditSortOrder, handleSort: handleAuditSort, sortParams: auditSortParams } =
    useTableSort<AuditLogSortField>("createdAt", "desc");

  const {
    data: user,
    isLoading: userLoading,
    error: userError,
  } = useQuery({
    queryKey: ["user", id],
    queryFn: () => userAPI.getUserById(id!),
    enabled: !!id,
  });

  const auditQueryKey = useMemo(
    () => ["user-audit-logs", id, auditPage, auditRowsPerPage, auditSortBy, auditSortOrder],
    [id, auditPage, auditRowsPerPage, auditSortBy, auditSortOrder]
  );

  const {
    data: auditData,
    isLoading: auditLoading,
    isFetching: auditFetching,
    error: auditError,
  } = useQuery({
    queryKey: auditQueryKey,
    queryFn: () =>
      auditLogsAPI.getAuditLogs({
        userId: id!,
        pageNo: auditPage + 1,
        pageSize: auditRowsPerPage,
        ...auditSortParams,
      }),
    enabled: !!id && canViewAuditLogs,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const auditRows = auditData?.data ?? [];
  const auditTotal = auditData?.total ?? 0;
  const auditBusy = auditLoading || auditFetching;
  const onAuditSort = (field: AuditLogSortField) =>
    handleAuditSort(field, () => setAuditPage(0));

  const isFieldStaffUser =
    user?.role === "OPERATOR" || user?.role === "MERCHANT";

  const {
    data: pendingSettlement,
    isLoading: settlementLoading,
    error: settlementError,
  } = useQuery({
    queryKey: ["settlement-pending", id],
    queryFn: () => settlementAPI.getPending(id!),
    enabled:
      !!id &&
      canViewAuditLogs &&
      (user?.role === "OPERATOR" || user?.role === "MERCHANT"),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const settleMutation = useMutation({
    mutationFn: () => settlementAPI.settle(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settlement-pending", id] });
      queryClient.invalidateQueries({ queryKey: ["user-audit-logs", id] });
    },
  });

  const targetRole = String(user?.role || "").toUpperCase();
  const targetUsesMpin = MPIN_ROLES.has(targetRole);
  const canResetThisUser =
    canResetCredentials &&
    canUpdate("users") &&
    !!user &&
    currentUser?.id !== user.id;

  const resetCredentialsMutation = useMutation({
    mutationFn: async () => {
      if (!id || !user) throw new Error("User missing");
      if (targetUsesMpin) {
        const mpin = newMpin.trim();
        if (!/^\d{6}$/.test(mpin)) {
          throw new Error("MPIN must be exactly 6 digits");
        }
        return userAPI.updateUser(id, { mpin });
      }
      const pwd = newPassword.trim();
      if (pwd.length < 8) {
        throw new Error("Password must be at least 8 characters");
      }
      return userAPI.updateUser(id, { password: pwd });
    },
    onSuccess: (updated) => {
      const once = updated?.temporaryPassword
        ? ` New password (shown once): ${updated.temporaryPassword}`
        : updated?.mpin
          ? " MPIN updated."
          : "";
      setResetMsg({
        type: "success",
        text: `Credentials reset.${once}`,
      });
      setResetOpen(false);
      setNewPassword("");
      setNewMpin("");
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: any) => {
      setResetMsg({
        type: "error",
        text: e?.response?.data?.message || e?.message || "Reset failed",
      });
    },
  });

  if (userLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress />
      </Box>
    );
  }

  if (userError || !user) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/users")}
          sx={{ mb: 2 }}
        >
          Back to users
        </Button>
        <Alert severity="error">
          {(userError as Error)?.message || "User not found"}
        </Alert>
      </Box>
    );
  }

  const branch = (user as any).branch;
  const salesPoint = (user as any).salesPoint;

  return (
    <Box>
      <PageHeader
        title={user.name}
        subtitle="User profile & activity"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Users", to: "/users" },
          { label: user.name },
        ]}
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/users")}
            >
              Back
            </Button>
            {canResetThisUser && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<LockResetIcon />}
                onClick={() => {
                  setResetMsg(null);
                  setNewPassword("");
                  setNewMpin("");
                  setResetOpen(true);
                }}
              >
                {targetUsesMpin ? "Set MPIN" : "Reset password"}
              </Button>
            )}
            {canUpdate("users") && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() =>
                  navigate("/users", { state: { editUserId: id } })
                }
              >
                Edit
              </Button>
            )}
          </Stack>
        }
      />

      {resetMsg && (
        <Alert
          severity={resetMsg.type}
          sx={{ mb: 2 }}
          onClose={() => setResetMsg(null)}
        >
          {resetMsg.text}
        </Alert>
      )}
      <Card sx={{ mb: 3, borderRadius: tokens.radius.md }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <HolderAvatar name={user.name} size={72} />
            <Box flex={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h5" fontWeight={700}>
                  {user.name}
                </Typography>
                <Chip
                  label={formatRoleLabel(user.role)}
                  size="small"
                  color={roleColor(user.role) as any}
                />
                <Chip
                  label={user.isActive ? "Active" : "Inactive"}
                  size="small"
                  color={user.isActive ? "success" : "default"}
                  variant="outlined"
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <PhoneIcon color="action" sx={{ mt: 0.3 }} />
                <DetailRow label="Phone" value={user.phone || "—"} />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <BranchIcon color="action" sx={{ mt: 0.3 }} />
                <DetailRow label="Branch" value={branch?.name || "—"} />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <SalesPointIcon color="action" sx={{ mt: 0.3 }} />
                <DetailRow label="Sales point" value={salesPoint?.name || "—"} />
              </Stack>
            </Grid>
            {(user.role === "OPERATOR" || user.role === "MERCHANT") && (
              <Grid item xs={12} sm={6} md={4}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <PersonIcon color="action" sx={{ mt: 0.3 }} />
                  <DetailRow
                    label="MPIN"
                    value={
                      <Typography variant="body2" color="text.secondary">
                        Hidden — reset to issue a new PIN
                      </Typography>
                    }
                  />
                </Stack>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <DetailRow
                label="Last login"
                value={
                  user.lastLogin ? formatWhen(user.lastLogin) : "Never"
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DetailRow
                label="Created"
                value={user.createdAt ? formatWhen(user.createdAt) : "—"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DetailRow
                label="Updated"
                value={user.updatedAt ? formatWhen(user.updatedAt) : "—"}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {canViewAuditLogs && isFieldStaffUser && (
        <Card sx={{ mb: 3, borderRadius: tokens.radius.md }}>
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
              mb={2}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SettlementIcon color="warning" />
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Pending settlement
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Day-end collection — after settle, this user&apos;s app shows a clean dashboard
                  </Typography>
                </Box>
              </Stack>
              {pendingSettlement?.hasPending && (
                <Button
                  variant="contained"
                  color="warning"
                  disabled={settleMutation.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Settle ${formatInr(pendingSettlement.amountToCollect)} collected from ${user.name}? Their pending sales/recharges will be archived in the database but hidden in the app.`
                      )
                    ) {
                      settleMutation.mutate();
                    }
                  }}
                >
                  {settleMutation.isPending ? "Settling…" : "Mark as settled"}
                </Button>
              )}
            </Stack>

            {settlementError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {(settlementError as Error).message || "Failed to load settlement"}
              </Alert>
            )}
            {settleMutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {(settleMutation.error as Error).message || "Settlement failed"}
              </Alert>
            )}
            {settleMutation.isSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Settlement completed. Dashboard will show no old sales/transactions for this user.
              </Alert>
            )}

            {settlementLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
              </Box>
            ) : pendingSettlement ? (
              <SettlementSummary pending={pendingSettlement} />
            ) : null}
          </CardContent>
        </Card>
      )}

      {canViewAuditLogs ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: tokens.radius.md,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <HistoryIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Activity log
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Actions performed by this user
            </Typography>
          </Box>

          {auditError && (
            <Alert severity="error" sx={{ m: 2 }}>
              {(auditError as any)?.response?.data?.message ||
                (auditError as Error).message ||
                "Failed to load audit logs"}
            </Alert>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableTableCell label="When" field="createdAt" sortBy={auditSortBy} sortOrder={auditSortOrder} onSort={onAuditSort} />
                  <SortableTableCell label="Action" field="action" sortBy={auditSortBy} sortOrder={auditSortOrder} onSort={onAuditSort} />
                  <SortableTableCell label="Resource" field="resource" sortBy={auditSortBy} sortOrder={auditSortOrder} onSort={onAuditSort} />
                  <SortableTableCell label="Method" field="httpMethod" sortBy={auditSortBy} sortOrder={auditSortOrder} onSort={onAuditSort} />
                  <SortableTableCell label="Path" field="path" sortBy={auditSortBy} sortOrder={auditSortOrder} onSort={onAuditSort} />
                  <SortableTableCell label="Status" field="success" sortBy={auditSortBy} sortOrder={auditSortOrder} onSort={onAuditSort} />
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditBusy ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : auditRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No activity recorded yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  auditRows.map((row: AuditLogEntry) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatWhen(row.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Chip label={row.action} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{row.resource}</TableCell>
                      <TableCell>{row.httpMethod}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={row.path}>
                          {row.path}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.success ? "OK" : "Fail"}
                          size="small"
                          color={row.success ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography
                          variant="caption"
                          title={
                            row.errorMessage ||
                            summaryBody(row.requestBody)
                          }
                        >
                          {row.errorMessage || summaryBody(row.requestBody)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            count={auditTotal}
            page={auditPage}
            onPageChange={(_, p) => setAuditPage(p)}
            rowsPerPage={auditRowsPerPage}
            onRowsPerPageChange={(e) => {
              setAuditRowsPerPage(parseInt(e.target.value, 10));
              setAuditPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      ) : (
        <Alert severity="info">
          Activity logs are available to Super Admin and Branch Admin only.
        </Alert>
      )}

      <Dialog
        open={resetOpen}
        onClose={() => !resetCredentialsMutation.isPending && setResetOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {targetUsesMpin ? "Set MPIN" : "Reset password"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {targetUsesMpin
              ? `Enter a new 6-digit MPIN for ${user.name}. The current MPIN is never shown.`
              : `Set a new password for ${user.name} (${formatRoleLabel(user.role)}). It will be shown once after save.`}
          </Typography>
          {targetUsesMpin ? (
            <TextField
              autoFocus
              fullWidth
              label="New MPIN"
              value={newMpin}
              onChange={(e) =>
                setNewMpin(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              inputProps={{
                maxLength: 6,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              helperText="Exactly 6 digits"
            />
          ) : (
            <TextField
              autoFocus
              fullWidth
              type="password"
              label="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Minimum 8 characters"
              autoComplete="new-password"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResetOpen(false)}
            disabled={resetCredentialsMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={resetCredentialsMutation.isPending}
            onClick={() => resetCredentialsMutation.mutate()}
          >
            {resetCredentialsMutation.isPending
              ? "Saving…"
              : targetUsesMpin
                ? "Save MPIN"
                : "Save password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const SettlementSummary: React.FC<{ pending: PendingSettlement }> = ({
  pending,
}) => {
  if (!pending.hasPending) {
    return (
      <Alert severity="success" icon={false}>
        No pending settlement — dashboard is clear for this user.
      </Alert>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={4} md={3}>
        <Typography variant="caption" color="text.secondary">
          Sales (unsettled)
        </Typography>
        <Typography fontWeight={700}>
          {pending.salesCount} · {formatInr(pending.salesAmount)}
        </Typography>
      </Grid>
      {pending.user.role === "OPERATOR" && (
        <Grid item xs={6} sm={4} md={3}>
          <Typography variant="caption" color="text.secondary">
            Recharges (unsettled)
          </Typography>
          <Typography fontWeight={700}>
            {pending.rechargesCount} · {formatInr(pending.rechargesAmount)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={6} sm={4} md={3}>
        <Typography variant="caption" color="text.secondary">
          Transactions (unsettled)
        </Typography>
        <Typography fontWeight={700}>
          {pending.transactionsCount} · {formatInr(pending.transactionsAmount)}
        </Typography>
      </Grid>
      <Grid item xs={6} sm={4} md={3}>
        <Typography variant="caption" color="text.secondary">
          Cash to collect
        </Typography>
        <Typography fontWeight={700} color="warning.main">
          {formatInr(pending.amountToCollect)}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default UserProfilePage;
